/**
 * Audio.
 *
 * Manifest-driven and silent by default. Every sound is referenced by a TAG
 * ('ceramic_shatter', 'granny_gasp'), and a tag with no file attached is simply a
 * no-op. That means the game runs with zero audio files today and gains sound
 * purely by dropping files in and editing the manifest - the same contract the 3D
 * assets use.
 *
 * This matters because the plan is for the kids to voice the cat and granny, and
 * for the user to produce the SFX properly later. Nothing in the code should have
 * to change when that happens.
 *
 * The positional path deliberately shares the SAME NoiseEvent that drives
 * granny's hearing (see core/events.ts). What the player hears is what granny
 * hears. Never add a loud sound without a matching noise event, or vice versa.
 */

import { Howl } from 'howler';
import type { EventBus, NoiseEvent } from '@/core/events';
import type { Vec3 } from '@/core/math';
import { distance } from '@/core/math';

export interface SoundDef {
  tag: string;
  /** Path under /public/assets/audio/. Null means "not recorded yet". */
  src: string | null;
  volume?: number;
  /** Randomized playback rate range, so repeats do not sound mechanical. */
  rateRange?: [number, number];
  loop?: boolean;
}

/**
 * The sound table. Every tag used anywhere in the game must appear here, even
 * with src:null - that way `npm run check` could later assert there are no
 * unknown tags, and this doubles as the recording checklist for the kids.
 */
export const SOUNDS: SoundDef[] = [
  // Cat movement
  { tag: 'silence', src: null },
  { tag: 'paw_soft', src: null, volume: 0.25 },
  { tag: 'paw_walk', src: null, volume: 0.4 },
  { tag: 'paw_run', src: null, volume: 0.6 },
  { tag: 'dash', src: null, volume: 0.7 },
  { tag: 'pounce', src: null, volume: 0.6 },
  { tag: 'land', src: null, volume: 0.5 },
  { tag: 'land_hard', src: null, volume: 0.8 },
  { tag: 'tumble', src: null, volume: 0.7 },

  // Cat voice - KIDS RECORD THESE. Highest-value audio in the project.
  { tag: 'meow', src: null, rateRange: [0.92, 1.08] },
  { tag: 'purr', src: null, loop: true },
  { tag: 'hiss', src: null },
  { tag: 'chirp', src: null },

  // Props
  { tag: 'thud_soft', src: null, rateRange: [0.9, 1.1] },
  { tag: 'ceramic_shatter', src: null, rateRange: [0.9, 1.15] },
  { tag: 'crash_huge', src: null },
  { tag: 'pot_crack', src: null },
  { tag: 'fabric_rip', src: null },
  { tag: 'paper_unroll', src: null },
  { tag: 'water_running', src: null, loop: true },
  { tag: 'fridge_open', src: null },
  { tag: 'chomp', src: null },
  { tag: 'scramble', src: null },
  { tag: 'wobble', src: null, volume: 0.35 },

  // Granny - KIDS RECORD THESE TOO. Gibberish beats real lines: funnier, ages
  // better, and trivially recordable at the kitchen table.
  { tag: 'granny_gasp', src: null },
  { tag: 'granny_yelp', src: null },
  { tag: 'granny_grumble', src: null },
  { tag: 'granny_wheeze', src: null },
  { tag: 'granny_aha', src: null },
  { tag: 'spray_hiss', src: null },
  { tag: 'slippers', src: null, volume: 0.4 },

  // UI
  { tag: 'key_collect', src: null },
  { tag: 'objective_done', src: null },
  { tag: 'cozy_moment', src: null },
  { tag: 'level_win', src: null },
  { tag: 'level_fail', src: null },
];

interface LoadedSound {
  def: SoundDef;
  howl: Howl | null;
}

export class AudioManager {
  private sounds = new Map<string, LoadedSound>();
  private listener: Vec3 = { x: 0, y: 0, z: 0 };
  private unsubscribe: (() => void) | null = null;
  private muted = false;
  /** Beyond this distance a positional sound is not played at all. */
  private maxAudibleDistance = 22;

  constructor(private readonly basePath = '/assets/audio/') {
    for (const def of SOUNDS) {
      this.sounds.set(def.tag, {
        def,
        howl: def.src
          ? new Howl({
              src: [`${this.basePath}${def.src}`],
              volume: def.volume ?? 1,
              loop: def.loop ?? false,
              // A missing file must never break the game.
              onloaderror: () => console.info(`[cat-game] audio "${def.tag}" not present yet`),
            })
          : null,
      });
    }
  }

  /** Mirrors every NoiseEvent into the mix. Call once at startup. */
  attach(bus: EventBus): void {
    this.unsubscribe = bus.on('noise', this.onNoise);
  }

  detach(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  setListener(position: Vec3): void {
    this.listener = position;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  private onNoise = (event: NoiseEvent): void => {
    // Loudness 0 means "no sound", which every idle step produces.
    if (event.loudness <= 0) return;
    this.playAt(event.tag, event.position, event.loudness / 100);
  };

  /** Plays a tag at a world position, attenuated by distance. */
  playAt(tag: string, position: Vec3, gain = 1): void {
    if (this.muted) return;
    const entry = this.sounds.get(tag);
    if (!entry) {
      console.warn(`[cat-game] unknown audio tag "${tag}" - add it to SOUNDS in audio.ts`);
      return;
    }
    if (!entry.howl) return; // Not recorded yet. Silent, by design.

    const dist = distance(this.listener, position);
    if (dist > this.maxAudibleDistance) return;

    const attenuation = Math.max(0, 1 - dist / this.maxAudibleDistance);
    const id = entry.howl.play();
    entry.howl.volume((entry.def.volume ?? 1) * gain * attenuation, id);

    if (entry.def.rateRange) {
      const [min, max] = entry.def.rateRange;
      entry.howl.rate(min + Math.random() * (max - min), id);
    }
  }

  /** Non-positional playback, for UI. */
  playUi(tag: string): void {
    if (this.muted) return;
    const entry = this.sounds.get(tag);
    entry?.howl?.play();
  }

  /** Tags still awaiting a recording. Shown in the debug HUD. */
  get missingCount(): number {
    return [...this.sounds.values()].filter((entry) => !entry.howl).length;
  }
}
